import React, { useState } from "react";
import { Connection, ConnectionConfiguration, Provider, ProviderType } from "../common/ObjectTypes";
import SchemaForm from "../common/SchemaForm";
import Spinner from "../common/Spinner";
import { ArrowDown, ArrowRight } from "../common/ProjectIcons";

type ConnectionDetailsProviderProps = {
    connection: Connection,
    rsjfErrors: any,
    connectionProvider: Provider,
    selectedProviderType: ProviderType,
    onChangeProviderEndpoint: Function,
    onChangeProviderConfigurations: Function,
    onChangeProviderEnabled: Function,
    onTestAction: Function,
    testActionLoading: boolean,
    testActionResponse: any,
    testActionStatus: number
}

const ConnectionDetailsProvider = ({
    connection,
    rsjfErrors,
    connectionProvider,
    selectedProviderType,
    onChangeProviderEndpoint,
    onChangeProviderConfigurations,
    onChangeProviderEnabled,
    onTestAction,
    testActionLoading,
    testActionResponse,
    testActionStatus
}: ConnectionDetailsProviderProps) => {
    const [isResponseVisible, setResponseVisible] = useState(false);
    const [activeActionId, setActiveActionId] = useState(null);
    const [showAdvanced, setShowAdvanced] = useState(false);

    function getButtonText(value: string): string {
        switch (value) {
            case 'auth':
                return 'Test Connection';
            case 'fetch_samples':
                return 'Fetch Data';
            default:
                return 'Test Action';
        }
    }

    function getResponseText(value: string, testActionResponse: any): any[] {
        switch (value) {
            case 'auth':
                return testActionResponse.valid_credentials ? ['Valid Credentials', true] : ['Invalid Credentials', false];
            case 'fetch_samples':
                return (testActionResponse.observations.length > 0) ? ['Fetch Successful', true] : ['Fetch Unsuccessful', false];
            default:
                return ['Response Received', null];
        }
    }

    return (
        <div className="mt-8 bg-very-light-gray">
            {/* div with text */}
            <div className="flex flex-row justify-between items-center w-full">
                <label className="text-secondary-gray">The following inputs connect Gundi to the account that provides the data.</label>
            </div>
            {/* div with configuration details */}
            <div className="flex flex-col w-full pb-52">
                {/* endpoint, dynamic form */}
                <div className="mt-3 flex flex-row">
                    {/* endpoint */}
                    <div className="flex flex-col w-1/2 mx-3 my-3">
                        {/* <div className="font-bold text-secondary-gray ">Configurations</div> */}
                        <div className="pl-8">
                            {   /* schema form */}
                            {selectedProviderType.actions.map((item: any) => {
                                return (
                                    <div key={item.id} className={(Object.keys(item.schema).length !== 0) ? "pb-14" : "hidden"}>
                                        {(Object.keys(item.schema).length !== 0) && <SchemaForm
                                            formTitle={item.name}
                                            schema={item.schema}
                                            formdata={connectionProvider.configurations && connectionProvider.configurations.findIndex((a: ConnectionConfiguration) => a.action.id === item.id) !== -1 ? connectionProvider.configurations.find((a: ConnectionConfiguration) => a.action.id === item.id).data : {}}
                                            onChange={(e: any) => onChangeProviderConfigurations(e, item.id)}
                                            isEditable={true}
                                            errors={rsjfErrors[item.id]}
                                        />}
                                        {item.schema.hasOwnProperty('is_executable') && item.schema.is_executable === true &&
                                            <div className="flex flex-col">
                                                <div className="flex flex-row items-center">
                                                    <button className="opacity-100 bg-light-gray hover:bg-gray-lines text-dark-gray font-semibold rounded py-2 px-4 ml-1 mr-3 shadow"
                                                        onClick={() => {
                                                            onTestAction(item.id, item.value)
                                                            setActiveActionId(item.id);
                                                        }}>
                                                        {getButtonText(item.value)}
                                                    </button>
                                                    {testActionLoading && activeActionId === item.id && <Spinner />}
                                                    {!testActionLoading && testActionResponse !== null && activeActionId === item.id && <p className={getResponseText(item.value, testActionResponse)[1] !== null ? (getResponseText(item.value, testActionResponse)[1] ? "bg-light-green p-2 rounded-sm text-dark-green" : "bg-very-light-orange p-2 rounded-sm text-dark-gray") : "bg-light-gray p-2 rounded-sm text-dark-gray"}>
                                                        {getResponseText(item.value, testActionResponse)[0]}
                                                    </p>}
                                                </div>
                                                {!testActionLoading && testActionResponse !== null && activeActionId === item.id &&
                                                    <div className="flex flex-col items-start">
                                                        <button className="ml-1" onClick={() => setResponseVisible(!isResponseVisible)}>
                                                            View Details {isResponseVisible ? <ArrowDown /> : <ArrowRight />}
                                                        </button>
                                                        {isResponseVisible &&
                                                            <pre className={getResponseText(item.value, testActionResponse)[1] !== null ? (getResponseText(item.value, testActionResponse)[1] ? "bg-light-green p-2 rounded-sm text-dark-green" : "bg-very-light-orange p-2 rounded-sm text-dark-gray") : "bg-light-gray p-2 rounded-sm text-dark-gray"}>
                                                                {JSON.stringify(testActionResponse, null, 2)}
                                                            </pre>
                                                        }
                                                    </div>
                                                }
                                            </div>}
                                    </div>
                                )
                            })}
                            {/* checkbox */}
                            <div className="flex flex-row items-start mt-6">
                                <input
                                    className="mt-1.5 accent-dark-green cursor-pointer"
                                    type="checkbox"
                                    value=""
                                    id="checkboxDefault"
                                    checked={connectionProvider.enabled}
                                    onChange={(e) => onChangeProviderEnabled(e)} />
                                <div className="flex flex-col">
                                    <label
                                        className="inline-block mx-2"
                                    >
                                        Activate Provider
                                    </label>
                                    <label
                                        className="inline-block mx-2 text-sm text-secondary-gray"
                                    >
                                        This connection will get data from its provider.
                                    </label>
                                </div>
                            </div>
                            {/* advanced configurations */}
                            <div>
                                <div className="mt-6 text-lg font-semibold text-secondary-gray" onClick={() => setShowAdvanced(!showAdvanced)}>
                                    {showAdvanced ? <ArrowDown /> : <ArrowRight />} Advanced Configurations
                                </div>
                                {showAdvanced &&
                                    <div className="ml-8 flex flex-col pb-14">
                                        <label
                                            className="inline-block hover:cursor-pointer text-sm text-secondary-gray"
                                        >
                                            Endpoint
                                        </label>
                                        <input
                                            id="endpoint"
                                            placeholder=""
                                            value={connectionProvider.base_url}
                                            className="p-1.5 h-auto border border-neutral-gray-200 rounded mb-3"
                                            onChange={(e) => onChangeProviderEndpoint(e)}
                                            disabled={false}
                                        >
                                        </input>
                                    </div>}
                            </div>

                        </div>
                    </div>
                </div>


            </div>

        </div>
    )
};

export default ConnectionDetailsProvider;